/**
 * Proveedor de autenticación embebida (ROPC contra /api/v1/auth/*).
 *
 * El SPA habla con el backend, que reenvía las credenciales a Keycloak
 * vía Direct Access Grants. El usuario no sale de la app. Es el proveedor
 * por defecto y el fallback de desarrollo.
 *
 * Implementa el contrato común `AuthProvider` de `./types`.
 */

import { jwtDecode } from "jwt-decode";
import { AuthError, type AuthProvider, type AuthUser } from "./types";
import {
  clearTokens,
  readTokens,
  storeTokens,
  type StoredTokens,
} from "./storage";

interface KeycloakJWT {
  sub: string;
  email?: string;
  name?: string;
  preferred_username?: string;
  exp: number; // epoch seconds
  realm_access?: { roles?: string[] };
}

interface TokenResponse {
  access_token: string;
  refresh_token: string;
  expires_in: number; // seconds
  refresh_expires_in?: number;
  token_type?: string;
}

// En dev local el frontend corre en :3000 con proxy de Vite hacia :8000,
// así que el path relativo funciona. En producción (mismo dominio detrás
// de nginx) VITE_API_URL puede quedar vacío.
const API_BASE = `${import.meta.env.VITE_API_URL || ""}/api/v1/auth`;

// Margen para renovar el token antes de que caduque (en segundos).
const REFRESH_THRESHOLD_SEC = 60;

let inFlightRefresh: Promise<string | null> | null = null;

async function parseError(resp: Response): Promise<string> {
  try {
    const body = await resp.json();
    if (typeof body.detail === "string") return body.detail;
  } catch {
    /* ignore */
  }
  return "Error de autenticación";
}

function tokensFromResponse(data: TokenResponse): StoredTokens {
  return {
    accessToken: data.access_token,
    refreshToken: data.refresh_token,
    expiresAt: Date.now() + data.expires_in * 1000,
  };
}

function userFromAccessToken(token: string): AuthUser {
  const decoded = jwtDecode<KeycloakJWT>(token);
  return {
    id: decoded.sub,
    email: decoded.email ?? "",
    name: decoded.name ?? decoded.preferred_username ?? "",
    roles: decoded.realm_access?.roles ?? [],
  };
}

async function login(username: string, password: string): Promise<AuthUser> {
  const resp = await fetch(`${API_BASE}/login`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ username, password }),
  });
  if (!resp.ok) {
    throw new AuthError(resp.status, await parseError(resp));
  }
  const data: TokenResponse = await resp.json();
  storeTokens(tokensFromResponse(data));
  return userFromAccessToken(data.access_token);
}

async function logout(): Promise<void> {
  const tokens = readTokens();
  clearTokens();
  if (!tokens) return;
  // Fire-and-forget: localmente las credenciales ya están borradas.
  try {
    await fetch(`${API_BASE}/logout`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ refresh_token: tokens.refreshToken }),
    });
  } catch {
    /* ignore network errors during logout */
  }
}

/**
 * Si el access_token está cerca de caducar, lo renueva con el
 * refresh_token. Devuelve el access_token vigente o null si ya no
 * podemos renovar.
 *
 * Coalescing: si dos componentes piden refresh a la vez, solo lanzamos
 * una llamada y ambos esperan al mismo resultado.
 */
async function refreshIfNeeded(): Promise<string | null> {
  const tokens = readTokens();
  if (!tokens) return null;

  const remainingMs = tokens.expiresAt - Date.now();
  if (remainingMs > REFRESH_THRESHOLD_SEC * 1000) {
    return tokens.accessToken;
  }
  if (inFlightRefresh) return inFlightRefresh;

  inFlightRefresh = (async () => {
    try {
      const resp = await fetch(`${API_BASE}/refresh`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ refresh_token: tokens.refreshToken }),
      });
      if (!resp.ok) {
        clearTokens();
        return null;
      }
      const data: TokenResponse = await resp.json();
      storeTokens(tokensFromResponse(data));
      return data.access_token;
    } catch {
      clearTokens();
      return null;
    } finally {
      inFlightRefresh = null;
    }
  })();

  return inFlightRefresh;
}

function getAccessToken(): string | null {
  return readTokens()?.accessToken ?? null;
}

function getCurrentUser(): AuthUser | null {
  const tokens = readTokens();
  if (!tokens) return null;
  try {
    return userFromAccessToken(tokens.accessToken);
  } catch {
    return null;
  }
}

/**
 * Llamar al cargar la app. Si hay tokens en sessionStorage, intenta
 * dejarlos vigentes (refresh si están cerca de caducar) y devuelve al
 * usuario. Si no hay nada o ya no se puede renovar, devuelve null.
 */
async function restoreSession(): Promise<AuthUser | null> {
  const tokens = readTokens();
  if (!tokens) return null;
  // Refresh defensivo: cubre el caso de F5 con un token recién caducado.
  const token = await refreshIfNeeded();
  if (!token) return null;
  return userFromAccessToken(token);
}

export const keycloakProvider: AuthProvider = {
  kind: "keycloak",
  usesCredentialsForm: true,
  // ROPC no necesita inicialización asíncrona.
  initialize: async () => {},
  login,
  // En keycloak el login es por formulario; no hay flujo interactivo externo.
  loginInteractive: async () => {
    throw new AuthError(
      0,
      "El proveedor Keycloak usa login por formulario, no interactivo."
    );
  },
  logout,
  refreshIfNeeded,
  getAccessToken,
  getCurrentUser,
  restoreSession,
};
